import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import LRUCache from 'lru-cache';
import { AuxActionData, IntegrationAsyncOperationInf, IntegrationItemServiceInf, IntegrationProductFilterInf, StatusString, UserLicense } from '../interfaces/integrations';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from 'src/app/core/services/admin.service';
import { IntegPolicyService } from 'src/app/core/services/integ-policy.service';
import { Account, User } from '@mitel/cloudlink-sdk';
import { AccountsService } from './accounts.service';
import { SpinnerService } from './spinner.service';
import { WorkflowService } from './workflow.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';
import { StatusType } from '../constants/statusType.enum';
import { TranslateService } from '@ngx-translate/core';
import { ZappaPhoneConfigDialogComponent } from 'src/app/modules/integrations-applications/components/integ-config-dialogs/zappa-phone-config-dialog/zappa-phone-config-dialog.component';
import { TenantsService } from './tenants.service';
import { Tenant, TenantIntegrationStatusType, TenantOauthStatusType, TenantSyncStatusType, TenantType, ZappaTenantStatus } from '../interfaces/tenants';
import { ZappaAuxDialogComponent } from 'src/app/modules/integrations-applications/components/integ-aux-dialogs/zappa-aux-dialog/zappa-aux-dialog.component';
import { GenericFilterMenu } from './../../shared/components/generic-item-search-controls/generic-item-search-controls.component';
import { firstValueFrom } from 'rxjs';
import { MenuItem } from '../interfaces/menu-item';
import { ApplicationType, PBXSettingsType, productCodes, UserRoles, zappaPhoneCodes } from '../constants/constants';
import { UsersFilterType } from '../constants/user-filter-constants';
import { Application } from '../interfaces/system-manager';
import { SystemManagerService } from './system-manager.service';
import { OrdersService } from './orders.service';
import { PBXSettingsRequirements } from '../interfaces/pbx-settings-requirements';
import { ZappaAccountService } from './zappa-account.service';
import { AppStateService } from './app-state.service';

const logContext = 'IntegZappaPhoneService';

@Injectable({
	providedIn: 'root'
})

export class IntegZappaPhoneService implements IntegrationItemServiceInf, IntegrationProductFilterInf, IntegrationAsyncOperationInf {
	private appStateService: AppStateService;
	private adminService: AdminService;
	private policyService: IntegPolicyService;
	private accountsService: AccountsService;
	private spinnerService: SpinnerService;
	private workflowService: WorkflowService;
	public dialog: MatDialog;
	private rbacAuthGuardService: RBACAuthGuardService;
	private translateService: TranslateService;
	private tenantsService: TenantsService;
	private systemManagerService: SystemManagerService;
	private ordersService: OrdersService;
	private zappaAccountService: ZappaAccountService;

	//store applications needed to determine left menu items for PBX settings
	private applicationsCache = new LRUCache<string, Application[]>({
		ttl: 60 * 10 * 1000,  // ms (cache item expires in 10 min)
		max: 10          // and, oldest item in cache is bumped if more than this items in cache
		// https://github.com/isaacs/node-lru-cache#readme
	}); // cache by account ID

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.appStateService )
			this.appStateService = AppInjector.injector.get(AppStateService);
	
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if ( !this.workflowService )
			this.workflowService = AppInjector.injector.get(WorkflowService);
	
		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);
			
		if (!this.rbacAuthGuardService)
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

		if (!this.translateService)
			this.translateService = AppInjector.injector.get(TranslateService);

		if (!this.tenantsService)
			this.tenantsService = AppInjector.injector.get(TenantsService);

		if (!this.systemManagerService)
			this.systemManagerService = AppInjector.injector.get(SystemManagerService);

		if (!this.ordersService)
			this.ordersService = AppInjector.injector.get(OrdersService);

		if (!this.zappaAccountService)
			this.zappaAccountService = AppInjector.injector.get(ZappaAccountService);
	}

	async canBeSelected(account: Account): Promise<boolean>{
		this.initServices();

		let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONZAPPAPHONE, ActionType.VIEW);
		let isDeleteInProgress = await this.isAsyncInProgress(account); 
		return Promise.resolve(!isDenied && !isDeleteInProgress);
	}

	getConfigurationDialog() : any
	{
		return ZappaPhoneConfigDialogComponent;
	}

	canHaveAsyncOperations(account: Account): Promise<boolean> {
		return Promise.resolve(true);
	}

	isAsyncInProgress(account: Account):Promise<boolean> 
	{
		this.initServices();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.Zappa && tags.Zappa.async?.asyncDeleteJobId != undefined )
				return true;
			else
				return false;
		})
		.catch(err => {
			console.error(`${logContext}.isAsyncInProgress error trying to determine async state for Zappa=`, err);
			//throw(err);
			return false; // TBD: Is this what I should do?
		})
	}

	isDeleteAsync( account: Account ): Promise<boolean>
	{
		this.initServices();
		return this.isSetupComplete(account);
	}

	getAsyncJobIdsInProgress( account: Account ):Promise<string[]>
	{
		this.initServices();
		// there is only 1 possible async job we are concerned with
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.Zappa && tags.Zappa.async?.asyncDeleteJobId != undefined )
				return [tags.Zappa.async.asyncDeleteJobId];
			else
				return [];
		})
		.catch(err => {
			console.error(`${logContext}.isAsyncInProgress error trying to determine async state for Zappa=`, err);
			//throw(err);
			return []; // TBD: Is this what I should do?
		})
	}

	asyncJobIsComplete( account: Account, jobId: string )
	{
		this.initServices();
		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {
			let tags = newAccount.tags;
			if ( tags && tags.Zappa && tags.Zappa.async?.asyncDeleteJobId != jobId )
			{
				return; // nothing to do. This really should not happen, testing incase of a glare situation
			}
			// at this point the only async operation that is integration affecting is delete
			// continue with the delete
			return this.deleteIntegrationSync(newAccount, true).then(retVal => {
				return;
			})
		});
	}

	asyncJobFailed( account: Account, jobId: string, reason: any )
	{
		this.initServices();
		console.log("The async job failed", jobId, reason)
		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {
			let tags = newAccount.tags;
			if ( tags && tags.Zappa && tags.Zappa?.async?.asyncDeleteJobId != jobId )
			{
				return; // nothing to do. This really should not happen, testing incase of a glare situation
			}
			// at this point the only async operation that is integration affecting is delete
			// continue with the delete
			try 
			{
				if ( tags && tags.Zappa && tags.Zappa?.async?.asyncDeleteJobId ) { // just in case there is a tag without a tenant id

					// for now save the error. I just don't know when it would be cleared
					// but then does it matter when it is cleared? They were trying to delete the integration
					let dataChanges = {
						tags: tags
					}
					dataChanges.tags.Zappa.asyncDeleteError = reason;
					return this.adminService.updateAccount(account.accountId, dataChanges).then(async result => {
						this.accountsService.mergeAccountWithModel(result);
						await this.adminService.deleteAccountTag(account.accountId, 'Zappa.async');
						this.clearCompleteSetupCache(account);
						this.clearTenantCache(account);
						this.clearStatusMessageCache(account);
						return;
					});
				}
			}
			catch (e)
			{
				// delete is best effort ????
				console.error(`${logContext}.asyncJobFailed error during the handling of a failed delete, err=`, e);
			}
		})
		.catch (err => {
			console.error(`${logContext}.asyncJobFailed error failure getting the account, err=`, err);
			// not going to throw at this point, it will be invoked the next time
		});
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		this.initServices();
		if ( !this.zappaAccountService.completeSetupCache.has(account.accountId) )
		{
			const promise = this.getSetupCompleteValue( account ).then( setup => {
				return setup;
			})
			.catch(err => {
				this.clearCompleteSetupCache(account);  // remove rejected Promise from cache
				this.clearTenantCache(account);
				return false;
			})
			this.zappaAccountService.completeSetupCache.set(account.accountId, promise);
		}
		return this.zappaAccountService.completeSetupCache.get(account.accountId);
	}

	getSetupCompleteValue(account: Account) : Promise<boolean>
	{
		this.initServices();

		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			this.setOrDeleteTenantCache(account.accountId, undefined);
			if ( tags && tags.Zappa && tags.Zappa.setupComplete == true )
			{
				// cache tenant when tenantId found in the tags
				// return true regardless ??
				if(tags.Zappa.tenantId) {
					return this.tenantsService.getTenantById(tags.Zappa.tenantId).then(tenant => {
						this.setOrDeleteTenantCache(account.accountId, tenant);
						return true;
					})
					.catch( err => {
						console.error(`${logContext}.getSetupCompleteValue Failed to get the tenant by Id for integration, err=`, err);
						throw err;
					});
				}
				return true;
			}
			else // suppose to check if there is a prexisting zoom tenant
			{
				return this.tenantsService.getTenantByType(TenantType.ZOOM).then(tenant => {
					if ( tenant?.tenantType == undefined )
					{
						return false; // should I be storing this so I don't keep requesting it? Or have a cache in the service?
					}
					// if we get here, the type should be zoom
					if ( tenant?.tenantType == TenantType.ZOOM )
					{
						let dataChanges: any = {
							tags: {
								Zappa: {
									setupComplete: true,
									tenantId: tenant.tenantId
								}
							}
						};

						this.setOrDeleteTenantCache(account.accountId, tenant);
						if(this.appStateService.getSupportPortalMode()){
							//not allowed to update tags
							this.spinnerService.hideSpinner();
							return true;
						}

						return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
							this.accountsService.mergeAccountWithModel(result);
							this.spinnerService.hideSpinner();
							return true;
						});
					}
				});
			}
		})
		.catch(err => {
			console.error(`${logContext}.getSetupCompleteValue error trying to determine complete setup state for Zappa=`, err);
			throw(err);
		})
	}

	private setOrDeleteTenantCache(accountId: string, tenant: Tenant){
		if(tenant){
			this.zappaAccountService.tenantCache.set(accountId, tenant);
		}
		else{
			this.zappaAccountService.tenantCache.delete(accountId);
		}
	}
	
	clearCompleteSetupCache( account: Account ) 
	{
		this.initServices();
		this.zappaAccountService.completeSetupCache.delete(account.accountId);
	}

	clearTenantCache( account: Account ) 
	{
		this.initServices();
		this.setOrDeleteTenantCache(account.accountId, undefined);
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		this.initServices();
		if ( state == false )
		{
			this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
			this.spinnerService.showSpinner();

			return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {

				// clean up the connector
				if ( newAccount.tags && newAccount.tags.Zappa?.tenantId ) // this means setup was complete
				{
					let asyncJobId;

					try 
					{
						const tenant = this.zappaAccountService.tenantCache.get(account.accountId);
						let retVal = await this.tenantsService.deleteTenant(newAccount.tags.Zappa.tenantId, tenant, '?mode=async');
						asyncJobId = retVal.jobId;
						this.clearStatusMessageCache(account);
					}
					catch (e)
					{
						console.error(`${logContext}.handleIntegrationStateChange error during the deletion of the Zappa phone integration=`, e);
					}

					// we have an async job id, save it in the tags
					let dataChanges = {
						tags: newAccount.tags
					}

					dataChanges.tags.Zappa.async = {
						asyncDeleteJobId: asyncJobId,
						asyncDateTime: new Date().getTime()
					}
					return this.adminService.updateAccount( account.accountId, dataChanges).then(async result => {
						this.accountsService.mergeAccountWithModel(result);
						// if there was a previous failed delete, remove the error
						if (newAccount.tags.Zappa.asyncDeleteError)
							await this.adminService.deleteAccountTag(account.accountId, 'Zappa.asyncDeleteError')
						this.spinnerService.hideSpinner();
						return true;
					});
				}
				else // sync delete - no tenant exists
				{
					return this.deleteIntegrationSync(newAccount, false).then(retVal => {
						this.spinnerService.hideSpinner();
						return retVal;
					})
				}
			
			}).catch(err => {
				console.error(`${logContext}.handleIntegrationStateChange Failed to get the account, err=`, err);
				this.spinnerService.hideSpinner();
				throw err;
			});
		}
		else 
		{
			// it appears old cache values are stcking around if there is an async delete followed immediately by an add
			// depending on the user actions. So we will clear the cache on an add just to have a clean state
			this.clearCompleteSetupCache(account);
			this.clearTenantCache(account);
			this.clearStatusMessageCache(account);
		}
	}

	async deleteIntegrationSync(account: Account, removeProductTag: boolean): Promise<boolean>
	{
		try {
			if ( removeProductTag )
			{
				let pos = account.tags?.products?.indexOf('Zappa')
				if ( pos > -1 )
				{
					let dataChanges = {
						tags: account.tags
					}

					dataChanges.tags.products.splice(pos, 1);
					let newAccount = await this.adminService.updateAccount( account.accountId, dataChanges);
					this.accountsService.mergeAccountWithModel(newAccount);
				}
			}
		}
		catch(err)
		{
			console.error(`${logContext}.deleteIntegrationSync Failed to update the account, err=`, err);
			// delete is best effort here
		}
		
		try 
		{
			if ( account.tags && account.tags.Zappa ) { // just in case there is a tag without a tenant id
				await this.adminService.deleteAccountTag( account.accountId, 'Zappa' );
			}
		}
		catch (e)
		{
			// delete is best effort ????
			console.error(`${logContext}.deleteIntegrationSync error during the deletion of the Zappa tags, err=`, e);
		}

		this.clearCompleteSetupCache(account);
		this.clearTenantCache(account);
		this.clearStatusMessageCache(account);
		return true;
	}

	async getStatusString(account: Account): Promise<StatusString> {
		this.initServices();
		if ( !this.zappaAccountService.statusMessageCache.has(account.accountId) ) 
		{
			const promise = this.getStatusStringValue(account).then( statusMessage => {
				return statusMessage
			});
			this.zappaAccountService.statusMessageCache.set(account.accountId, promise);
			// no testing for error as that is returned in an error message itself
		}
		return this.zappaAccountService.statusMessageCache.get(account.accountId);
	}

	async getStatusStringValue(account: Account): Promise<StatusString> 
	{
		let integStatus: StatusString = {
			status: 'integrations.zappa.status-message.not-setup-error', 
			type: StatusType.caution
		}
		// must check for setup complete this way because of the
		// automatic discovery of the complete setup
		return this.isSetupComplete(account).then( setup => {
			if ( setup == false )
			{
				return integStatus;
			}

			// if we get here we know that the setup is complete (there is a tenant)
			// so we need to determine what what state the tenant is in
			return this.adminService.getAccountTags( account.accountId ).then( tags => {
				if ( tags && tags.Zappa?.asyncDeleteError )
				{
					// there was a delete failure
					integStatus.type = StatusType.error;
					integStatus.status = this.translateService.instant( 'integrations.zappa.status-message.delete-failed') + ": " + tags.Zappa.asyncDeleteError;
					return integStatus;
				}

				if ( tags && tags.Zappa?.async?.asyncDeleteJobId )
				{
					// there is an async job in progress so that should be the error displayed
					integStatus.type = StatusType.error;
					let dateStr = new Date(tags.Zappa.async.asyncDateTime).toLocaleString();
					integStatus.status = this.translateService.instant( 'integrations.zappa.status-message.delete-in-progress', {dateStr: dateStr});
					return integStatus;
				}
				else
				{
					//if setup is true, it's unexpected to have no tenantId
					//it might not be stored in the tags if the tenant was first detected while logged 
					//into the Support Portal - and tags cannot be updated.
					//So we rely on a cache
					let tenant = this.zappaAccountService.tenantCache.get(account.accountId);
					if(tenant)
					{
						if ( tenant && tenant.status?.integration?.state == TenantIntegrationStatusType.NOT_CONNECTED )
							{
								integStatus.type = StatusType.error;
								integStatus.status = tenant.status?.integration?.details || 'integrations.zappa.status-message.integration.notConnected';
								return integStatus;	
						}
						
						if ( tenant && tenant.status?.oauth?.state != TenantOauthStatusType.AUTHORIZED )
						{
							integStatus.type = StatusType.error;
							integStatus.status = tenant.status?.oauth?.details || 'integrations.zappa.status-message.oauth.' + tenant.status.oauth.state;
							return integStatus;
						}	
						
						if ( tenant && tenant.status?.sync?.state == TenantSyncStatusType.FAILED )
						{
							integStatus.type = StatusType.error;
							integStatus.status = tenant.status?.sync?.details || 'integrations.zappa.status-message.sync.failed'
							return integStatus;
						}

						if ( tenant && tenant.status?.integration?.state == TenantIntegrationStatusType.CONNECTED )
						{
							integStatus.type = StatusType.normal;
							integStatus.status = 'integrations.zappa.status-message.integration-working';
							return integStatus;
						}
					}
				}
				integStatus.type = StatusType.error;
				integStatus.status = 'integrations.zappa.status-message.undetermined';
				return integStatus;
			})
		})
		.catch( err => {
			console.error(`${logContext}.getStatusStringValue Failed to get the status for integration, err=`, err);
			integStatus.type = StatusType.error;
			integStatus.status = 'integrations.zappa.status-message.undetermined';
			return integStatus;
		});
	}
		
	clearStatusMessageCache( account: Account ) 
	{
		this.initServices();
		this.zappaAccountService.statusMessageCache.delete(account.accountId);
	}

	getTenantData( account: Account ): Promise<Tenant | undefined>
	{
		this.initServices();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.Zappa?.tenantId ) 
			{
				return this.tenantsService.getTenantById(tags.Zappa.tenantId).then(tenant => {
					this.setOrDeleteTenantCache(account.accountId, tenant);
					return tenant;
				})
			}
			else
			{
				return undefined;
			}
		})
		.catch( err => {
			console.error(`${logContext}.getTenantData Error saving zappa connector err=`, err);
			throw err;
		});
	}

	saveTenantData(account: Account, tenantData: Tenant ): Promise<any>
	{
		this.initServices();
		// here we will create the connector 
		let dataChanges: any = {
			tags: {
				Zappa: {
					setupComplete: true
				}
			}
		}
		let savePromise;

		if ( tenantData.tenantId )
		{
			// update an existing tenant data
			console.log("update an existing tenant data")
			savePromise = this.tenantsService.updateTenant(tenantData);
		}
		else
		{
			// create the new tenant
			console.log("create a new tenant")
			savePromise = this.tenantsService.createTenant(tenantData);
		}
		return savePromise.then(async data => {
			dataChanges.tags.Zappa.tenantId = data.tenantId;
			this.clearCompleteSetupCache(account);
			this.clearTenantCache(account);
			this.clearStatusMessageCache(account);

			if ( tenantData.tenantId == undefined ) // need to update the tags
			{
				this.zappaAccountService.initializeAsyncEventMonitor();
				return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
					this.accountsService.mergeAccountWithModel(result);
					this.spinnerService.hideSpinner();
					return data;
				});
			}
			else
			{
				return data;
			}
		})
		.catch (err => {
			console.error(`${logContext}.saveTenantData Error saving zappa connector err=`, err);
			this.spinnerService.hideSpinner();
			throw err;
		})
	}

	getTenantStatus( tenantId: string ): Promise<ZappaTenantStatus>
	{
		this.initServices();
		// TBD - should this status be specific to the dialog? Unsure of the requirements
		return this.tenantsService.getTenantStatus( tenantId ).then( status => {
			return status;
		})
		.catch ( err => {
			console.error(`${logContext}.getTenantStatus Failed to get the tenant status, err=`, err);
			return undefined; //do not think this is important enough to show an error...or is it?
		})
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		return Promise.resolve(undefined);
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		this.initServices();
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_ZAPPA_CONFIRMATION } });
		return firstValueFrom(dialogRef.afterClosed()).then(result => {
			return result;
		});
	}
	async getLeftNavMenus(account: Account): Promise<MenuItem[]> {
		return this.isAsyncInProgress(account).then(asyncInProgress => {
			if (asyncInProgress) { // delete cannot be in progress
				return [];
			}

			return this.isSetupComplete(account).then(async complete => {
				if (!complete) { // setup has to be complete before we can display the menu
					return [];
				}

				const zappaSupportItem = {
					name: 'ZappaSupport',
					translationKey: 'ZappaSupport',
					id: 'zappaSupport',
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: `/accounts/${account.accountId}/zappa-support`,
					isFederatedSPA: true,
					parentId: 'support', // Zappa Support item will be nested under the Support menu item
				};
				
				let pbxSettingMenu = [];
				try {
					//check integration status first ??

					const applications = await this.getAllApplications(account) ?? [];
					// look through PBXSettingsRequirements to find ( if exists ) one setting that has all required components listed by system inventory
					const pbxSettingsReq = PBXSettingsRequirements.find(pbxSettings => pbxSettings.requiredComponentTypes.
						filter(reqApplicationType => !!applications?.find(application => application.applicationType == reqApplicationType))?.
							length == pbxSettings.requiredComponentTypes.length);

					switch (pbxSettingsReq?.applicationType) {
						case ApplicationType.MIVOICE_5000:
							pbxSettingMenu = [this.generatePBXSettingsMenuItem(account, 'MiVo5000Business', PBXSettingsType.MIVOICE_5000)];
							break;
						case ApplicationType.OPEN_SCAPE_VOICE:
							pbxSettingMenu = [this.generatePBXSettingsMenuItem(account, 'OpenScapeVoice', PBXSettingsType.OPEN_SCAPE_VOICE)];
							break;
						case ApplicationType.OPEN_SCAPE_4K:
							pbxSettingMenu = [this.generatePBXSettingsMenuItem(account, 'OpenScape4K', PBXSettingsType.OPEN_SCAPE_4K)];
							break;
						default:
							break;
					}
				}
				catch(error) {
					console.error(`${logContext}.getLeftNavMenus - Error determining PBX types for account`, error);
				}

				return [zappaSupportItem].concat(pbxSettingMenu);
			});
		});
	}

	getAuxAction(account: Account): Promise<any> 
	{
		this.initServices();

		return this.isSetupComplete(account).then( complete => {
			if (complete)
			{
				let actionData: AuxActionData = {
					statement:  'integrations.zappa.aux-dialog.statusSummaryDesc',
					actionLabel: 'integrations.zappa.aux-dialog.statusSummary',
					isReadOnly: true //never is there a create/edit/delete action triggered
				};
				return Promise.resolve(actionData);
			}
			else
				return undefined;
		});
	}

	async handleAuxAction(account: Account): Promise<any> 
	{
		this.initServices();

		// if we get here then we can show the Aux dialog
		let auxDialogRef = this.dialog.open(ZappaAuxDialogComponent, 
			{ 
				disableClose: true,
				autoFocus: false,
				minWidth: '500px',
				maxWidth: '975px',
				data: { account: account } 
			});
		await firstValueFrom(auxDialogRef.afterClosed());
		return true;
	}

	async getUserLicense(user: User, account: Account): Promise<UserLicense> {
		this.initServices();

		const licenses = await this.ordersService.getUsersLicensesFromCachedList(user.userId);
		let userLicense: UserLicense;

		// if there is a legit subscription license
		for (var i = 0; i < licenses.length; i++) {
			if (zappaPhoneCodes.USER_PRODUCT_NAME === licenses[i]) {
				userLicense = {
					code: zappaPhoneCodes.USER_PRODUCT,
					displayStringKey: 'product_licenses.zappaPhone.name'
				}
				return userLicense;
			}
		}
		return undefined;
	}

	canFilterUsersOnProduct?(account: Account): boolean {
		return true;
	}

	async filterUsersBasedOnProduct(account: Account, users: User[], filter: any[]): Promise<User[]> {
		this.initServices();
		let newUsersList = new Array<User>();
		
		for (var i = 0; i < users.length; i++) {
			const license = await this.getUserLicense(users[i], account);
			if (license) {
				newUsersList.push(users[i]);
			}
		}
		return newUsersList;
	}

	getProductFilterMenu? (): GenericFilterMenu {
		return {
			displayStr: zappaPhoneCodes.USER_PRODUCT_NAME,
			phraseStr: zappaPhoneCodes.USER_PRODUCT_NAME,
			id: zappaPhoneCodes.USER_PRODUCT,
			multipleSelection: false,
			data: {
			id: productCodes.ZAPPA,
				type: UsersFilterType.PRODUCT
			}
		};
	}

	async getAllApplications(account: Account): Promise<Application[]> {
		if (!this.applicationsCache.has(account.accountId)) {
			try {
				const applications = await this.getApplications();
				this.applicationsCache.set(account.accountId, applications);
			}
			catch (error) {
				this.applicationsCache.delete(account.accountId);
				console.error(`${logContext}.getAllApplications - Error determining system manager - applications`, error);
				throw error;
			}
		}
		console.debug("getAllApplications", this.applicationsCache.get(account.accountId));
		return this.applicationsCache.get(account.accountId);
	}

	async getApplications(): Promise<Application[]> {
		this.initServices();

		let applications = [];
		const componentsEmbedded = await this.systemManagerService.getComponents();
		if (componentsEmbedded?.length) {
			applications = componentsEmbedded.map(component => {
				return {
					applicationId: component.componentId,
					applicationType: component.componentType
				}
			});
		}
		return applications;
	}

	generatePBXSettingsMenuItem(account: Account, name: string, pbxType: PBXSettingsType) {
		const systemSettingsMenuItem = {
			name: `${name}SystemSettings`,
			translationKey: 'SystemSettings',
			id: `${name}SystemSettings`,
			minRole: UserRoles.ACCOUNT_ADMIN,
			navigation: `/accounts/${account.accountId}/${pbxType}/systemSettings`,
			iconActive: '../../../../assets/images/account_active.svg',
			iconInactive: '../../../../assets/images/account_inactive.svg',
			isFederatedSPA: true
		};

		const primaryMenuItem = {
			name: name,
			translationKey: name,
			id: name,
			minRole: UserRoles.ACCOUNT_ADMIN,
			navigation: '/norouting',
			iconActive: '../../../../assets/images/users_active.svg',
			iconInactive: '../../../../assets/images/users_inactive.svg',
			children: [
				systemSettingsMenuItem
			]
		};

		return primaryMenuItem;
	}
}
